import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Datenqualitaet } from "@/components/dashboard/Datenqualitaet"
import { Fahrweisen } from "@/components/dashboard/Fahrweisen"
import { FilterLeiste } from "@/components/dashboard/FilterLeiste"
import { JahresVergleich } from "@/components/dashboard/JahresVergleich"
import { Kennzahlen } from "@/components/dashboard/Kennzahlen"
import { TypenBalken } from "@/components/dashboard/TypenBalken"
import { Verlauf } from "@/components/dashboard/Verlauf"
import { Leer } from "@/components/ui"
import { api, fehlertext } from "@/lib/api"
import { auswahl, durchsatzListe, startFilter, type AuswertungsDaten, type Filter } from "@/lib/auswertung"

/** Neu zeichnen, wenn das Farbschema wechselt (Rampenfarben kommen aus CSS-Variablen). */
function useFarbschema() {
  const [, setZaehler] = useState(0)
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const neu = () => setZaehler((z) => z + 1)
    mq.addEventListener("change", neu)
    const mo = new MutationObserver(neu)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
    return () => { mq.removeEventListener("change", neu); mo.disconnect() }
  }, [])
}

export function DashboardPage() {
  useFarbschema()
  const abfrage = useQuery({
    queryKey: ["auswertung"],
    queryFn: async () => (await api.get<AuswertungsDaten>("/auswertung/daten")).data,
    refetchInterval: 5 * 60 * 1000,
  })
  const d = abfrage.data
  const [filter, setFilter] = useState<Filter | null>(null)
  useEffect(() => {
    if (d && !filter) setFilter(startFilter(d))
  }, [d, filter])

  const tp = useMemo(() => (d ? durchsatzListe(d) : new Float64Array()), [d])
  const anzahlTyp = useMemo(() => {
    if (!d) return []
    const c = new Array(d.dims.typ.length).fill(0)
    for (const t of d.rows.t) c[t]++
    return c
  }, [d])
  const ids = useMemo(() => (d && filter ? auswahl(d, filter) : []), [d, filter])
  const idsOhneTyp = useMemo(() => (d && filter ? auswahl(d, filter, { ohneTyp: true }) : []), [d, filter])
  const ausgeblendet = useMemo(
    () => (d && filter && !filter.ausr ? auswahl(d, filter, { nurAusr: true }).length : 0),
    [d, filter],
  )

  if (abfrage.isError) {
    return <main className="mx-auto max-w-[1260px] px-4 py-8"><Leer>{fehlertext(abfrage.error, "Die Auswertung konnte nicht geladen werden.")}</Leer></main>
  }
  if (!d || !filter) {
    return <main className="mx-auto max-w-[1260px] px-4 py-8"><Leer>Auswertung wird geladen …</Leer></main>
  }
  const quellen = Object.entries(d.meta.quellen)
    .map(([q, n]) => `${n.toLocaleString("de-DE")} ${q === "terminal" ? "am Terminal" : q === "extrakt" ? "aus Extrakten" : "aus dem Excel-Tagebuch"}`)
    .join(" · ")
  return (
    <>
      <div className="mx-auto grid max-w-[1260px] gap-1.5 px-4 pb-4 pt-6">
        <p className="m-0 font-mono text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted">Heinkel-Zentrifuge · Konti-Verdrängung</p>
        <h1 className="m-0 text-[clamp(24px,3.2vw,32px)] font-semibold leading-tight [text-wrap:balance]">Durchsatz NC feucht</h1>
        <p className="m-0 mt-1 max-w-[72ch] text-ink2">
          <b className="font-semibold text-ink">Durchsatz [kg/h] = Auswurfgewicht ÷ Zykluszeit × 3600</b>, je Probe aus dem
          Betriebstagebuch (etwa alle 2 Stunden pro Schicht). Gegliedert wie die Excel-Auswertung nach NC-Typ, Gebinde,
          Anfeuchtmittel, Anfeuchtung Soll und Jahr.
        </p>
        <p className="m-0 text-[12.5px] text-muted">
          {`Proben: ${quellen}${d.meta.letzte_probe ? ` · letzte Probe ${new Date(d.meta.letzte_probe).toLocaleDateString("de-DE")}` : ""}`}
        </p>
      </div>
      <FilterLeiste d={d} f={filter} setF={setFilter} anzahlTyp={anzahlTyp} />
      <main className="mx-auto grid max-w-[1260px] gap-[18px] px-4 pb-12 pt-5">
        <Kennzahlen d={d} tp={tp} ids={ids} f={filter} ausgeblendet={ausgeblendet} />
        <Verlauf d={d} tp={tp} ids={ids} f={filter} />
        <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.3fr)]">
          <TypenBalken d={d} tp={tp} ids={idsOhneTyp} f={filter} setTyp={(typ) => setFilter({ ...filter, typ })} />
          <JahresVergleich d={d} tp={tp} ids={idsOhneTyp} f={filter} />
        </div>
        <Fahrweisen d={d} tp={tp} ids={ids} f={filter} />
        <Datenqualitaet d={d} />
      </main>
    </>
  )
}
